/* eslint-disable import/extensions */
import { mapActions, mapGetters, mapState } from 'vuex';
import accountLogin from '@/components/account/account-login/account-login.vue';
import accountLoginFooter from '@/components/account/account-login/footer/footer.vue';
import accountRegister from '@/components/account/account-register/account-register.vue';
import accountRegisterFooter from '@/components/account/account-register/footer/footer.vue';
import { MODALS } from '@/components/app/app-modals/modals-constants';
import genericSmallPopup from '@/components/generics/modal/layouts/generic-small.vue';
import genericPopup from '@/components/generics/modal/layouts/generic.vue';
import modal from '@/components/generics/modal/modal.vue';
import smallModal from '@/components/generics/modal/small/small.vue';
import addFundsCombined from '@/components/wallet/add-funds-combined/add-funds-combined.vue';
import addFundsCombinedFooter from '@/components/wallet/add-funds-combined/footer/footer.vue';
import { VERSION_TO_MIGRATE_TO } from '@/components/what-is-new-popup/helpers';
import constants from '@/constants';
import { withMeta } from '@/helpers/dataLayer';
import { lazyComponents } from '@/helpers/reloadOnChunkError';
import loaderMixin from '@/mixins/loader';

// Lazy modal bodies, each wrapped so a failed chunk fetch (e.g. a stale client
// after a deploy) reloads the page once instead of silently failing to open.
const lazyModalBodies = lazyComponents({
  cmsOrderModal: () => import('@/components/cms/cms-order-modal/cms-order-modal.vue'),
  accountForgotten: () =>
    import(
      /* webpackChunkName: "modal-forgotten" */ '@/components/account/account-forgotten/account-forgotten.vue'
    ),
  accountForgottenFooter: () =>
    import(
      /* webpackChunkName: "modal-forgotten" */ '@/components/account/account-forgotten/footer/footer.vue'
    ),
  accountRegisterExternal: () =>
    import(
      /* webpackChunkName: "modal-register-external" */ '@/components/account/account-register-external/account-register-external.vue'
    ),
  accountDetailsVerify: () =>
    import(
      /* webpackChunkName: "modal-account-verify" */ '@/components/account/account-details/modal/account-details-verify.vue'
    ),
  accountUploadId: () =>
    import(
      /* webpackChunkName: "modal-upload-id" */ '@/components/account/account-upload-id/account-upload-id.vue'
    ),
  accountUploadDocument: () =>
    import(
      /* webpackChunkName: "modal-upload-document" */ '@/components/account/account-upload-document/account-upload-document.vue'
    ),
  accountDetailsUpdateModal: () =>
    import(
      /* webpackChunkName: "modal-account-details-update" */ '@/components/account/account-details/modal/account-details-update-modal.vue'
    ),
  accountUpdateImage: () =>
    import(
      /* webpackChunkName: "modal-update-image" */ '@/components/account/account-update-image/account-update-image.vue'
    ),
  deposited: () =>
    import(/* webpackChunkName: "modal-deposited" */ '@/components/wallet/deposited/deposited.vue'),
  potentialPayouts: () =>
    import(
      /* webpackChunkName: "modal-potential-payouts" */ '@/components/potential-payouts/potential-payouts.vue'
    ),
  exportHistory: () =>
    import(
      /* webpackChunkName: "modal-export-history" */ '@/components/export-history/export-history.vue'
    ),
  insightModal: () =>
    import(
      /* webpackChunkName: "modal-insight" */ '@/components/insight/insight-modal/insight-modal.vue'
    ),
  research: () =>
    import(/* webpackChunkName: "modal-research" */ '@/components/research/research.vue'),
  taxInfoNotification: () =>
    import(
      /* webpackChunkName: "modal-tax-info" */ '@/components/tax-info-notification/tax-info-notification.vue'
    ),
  twitterTimeline: () =>
    import(
      /* webpackChunkName: "modal-twitter" */ '@/components/twitter/twitter-timeline/twitter-timeline.vue'
    ),
  newsFeed: () =>
    import(
      /* webpackChunkName: "modal-news-feed" */ '@/components/home/home-old/news-feed/news-feed.vue'
    ),
  attachToHome: () =>
    import(
      /* webpackChunkName: "modal-attach-to-home" */ '@/components/attach-to-home/attach-to-home.vue'
    ),
  offersIndicatorPopup: () =>
    import(
      /* webpackChunkName: "modal-offers-indicator" */ '@/components/offers-indicator-popup/offers-indicator-popup.vue'
    ),
  marketContractsStatsPopup: () =>
    import(
      /* webpackChunkName: "modal-market-contracts-stats" */ '@/components/market/market-contracts-stats-popup/market-contracts-stats-popup.vue'
    ),
  accountResetPassword: () =>
    import(
      /* webpackChunkName: "modal-reset-password" */ '@/components/account/account-reset-password/account-reset-password.vue'
    ),
  orientationInfo: () =>
    import(
      /* webpackChunkName: "modal-orientation" */ '@/components/orientation-info/orientation-info.vue'
    ),
  accountBadgesList: () =>
    import(
      /* webpackChunkName: "modal-badges" */ '@/components/account/account-badges-list/account-badges-list.vue'
    ),
  userProfileOverview: () =>
    import(
      /* webpackChunkName: "modal-user-profile" */ '@/components/social/user-profile-overview/user-profile-overview.vue'
    ),
  userProfileOverviewHeader: () =>
    import(
      /* webpackChunkName: "modal-user-profile" */ '@/components/social/user-profile-overview-header/user-profile-overview-header.vue'
    ),
  userReport: () =>
    import(
      /* webpackChunkName: "modal-user-report" */ '@/components/social/user-report/user-report.vue'
    ),
  leaderboardsTrophyPyramid: () =>
    import(
      /* webpackChunkName: "modal-leaderboards-trophy" */ '@/components/leaderboards/leaderboards-trophy-pyramid/leaderboards-trophy-pyramid.vue'
    ),
  messagingJourneyInfo: () =>
    import(
      /* webpackChunkName: "modal-messaging-journey" */ '@/components/messaging-journey-info/messaging-journey-info.vue'
    ),
  coinbaseProcessOverview: () =>
    import(
      /* webpackChunkName: "modal-coinbase" */ '@/components/wallet/coinbase-process-overview/coinbase-process-overview.vue'
    ),
  announcement: () =>
    import(
      /* webpackChunkName: "modal-announcement" */ '@/components/announcement/announcement.vue'
    ),
  affiliationsPopup: () =>
    import(
      /* webpackChunkName: "modal-affiliations" */ '@/components/profile/affiliations-popup/affiliations-popup.vue'
    ),
  accountAlreadyExists: () =>
    import(
      /* webpackChunkName: "modal-account-already-exists" */ '@/components/account/account-already-exists/account-already-exists.vue'
    ),
  accountAlreadyExistsFooter: () =>
    import(
      /* webpackChunkName: "modal-account-already-exists" */ '@/components/account/account-already-exists/footer/footer.vue'
    ),
  accountEnableClassicPredictit: () =>
    import(
      /* webpackChunkName: "modal-enable-classic" */ '@/components/account/account-enable-classic-predictit/account-enable-classic-predictit.vue'
    ),
  accountEnableClassicPredictitFooter: () =>
    import(
      /* webpackChunkName: "modal-enable-classic" */ '@/components/account/account-enable-classic-predictit/footer/footer.vue'
    ),
  marketOwnershipConflictModal: () =>
    import(
      /* webpackChunkName: "modal-market-ownership-conflict" */ '@/components/market/market-ownership-conflict-modal/market-ownership-conflict-modal.vue'
    ),
  accountTwoFactorVerify: () =>
    import(
      /* webpackChunkName: "modal-two-factor" */ '@/components/account/account-two-factor-verify/account-two-factor-verify.vue'
    ),
  accountTwoFactorPrompt: () =>
    import(
      /* webpackChunkName: "modal-two-factor-prompt" */ '@/components/account/account-two-factor-prompt/account-two-factor-prompt.vue'
    ),
  accountPhoneUpdate: () =>
    import(
      /* webpackChunkName: "modal-phone-update" */ '@/components/account/account-phone-update/account-phone-update.vue'
    ),
  marketRules: () =>
    import(
      /* webpackChunkName: "modal-market-rules" */ '@/components/market/market-rules/market-rules.vue'
    ),
  purchaseReviewAndConfirmSushi: () =>
    import(
      /* webpackChunkName: "modal-purchase-review" */ '@/components/purchase/sushi/purchase-review-and-confirm-sushi/purchase-review-and-confirm-sushi.vue'
    ),
  shareInfoPopup: () =>
    import(
      /* webpackChunkName: "modal-share-info" */ '@/components/purchase/sushi/share-info-popup/share-info-popup.vue'
    ),
  captcha: () =>
    import(/* webpackChunkName: "modal-captcha" */ '@/components/forms/captcha/captcha.vue'),
  priceOddsTogglePopup: () =>
    import(
      /* webpackChunkName: "modal-price-odds" */ '@/components/market/price-odds-toggle-popup/price-odds-toggle-popup.vue'
    ),
  whatIsNewPopup: () =>
    import(
      /* webpackChunkName: "modal-whatisnew" */ '@/components/what-is-new-popup/what-is-new-popup.vue'
    ),
  whatIsNewPopupFooter: () =>
    import(
      /* webpackChunkName: "modal-whatisnew" */ '@/components/what-is-new-popup/what-is-new-popup-footer.vue'
    ),
});

export default {
  mixins: [loaderMixin],
  components: {
    modal,
    genericPopup,
    genericSmallPopup,
    smallModal,
    accountLogin,
    accountLoginFooter,
    accountRegister,
    accountRegisterFooter,
    addFundsCombined,
    addFundsCombinedFooter,
    ...lazyModalBodies,
  },
  computed: {
    ...mapState('app', ['isTaxNotificationMandatory']),
    ...mapState('popup', [
      'cmsOrder',
      'currentPopup',
      'currentSmallPopup',
      'currentPopupOptions',
      'lastTopPosition',
      'tradePopupOptions',
    ]),
    ...mapGetters('app', ['isShutdownModeOn', 'collegeAffiliationEnabled']),
    ...mapGetters('user', ['isUserRestricted']),
    ...mapState('user', ['status', 'registrationFlowActive', 'twoFactorEnabled']),
    canCloseAuth() {
      return this.$route.name !== 'disqus-auth';
    },
    footerHideBodyClass() {
      return this.currentPopupOptions.hideFooter
        ? 'modal__body--medium modal__footer--hide'
        : 'modal__body--medium';
    },
  },
  watch: {
    // Reset loading state when reCAPTCHA modal closes
    currentPopup(newVal) {
      if (newVal !== MODALS.RECAPTCHA_CHALLENGE) {
        this.recaptchaVerifying = false;
      }
    },
    // Reset loading state when popup options change (new v2 challenge)
    currentPopupOptions() {
      // Always reset verifying state when options change - this handles the case
      // where we show a new v2 challenge while the modal is already showing
      if (this.currentPopup === MODALS.RECAPTCHA_CHALLENGE) {
        this.recaptchaVerifying = false;
      }
    },
  },
  methods: {
    ...mapActions('app', ['setSettingsAnnouncementCurState']),
    ...mapActions('popup', ['setCurrentPopup', 'setCurrentSmallPopup', 'setCurrentPopupWithDelay']),
    ...mapActions('wallet', ['setCurrentDeposit', 'markFirstDepositDone']),
    ...mapActions('user', ['logout', 'endRegistrationFlow']),
    ...mapActions('overlay', { hideOverlay: 'hide' }),
    isCurrentModal(name) {
      return this.currentPopup === name;
    },
    isCurrentSmallModal(name) {
      return this.currentSmallPopup === name;
    },
    closeModal() {
      this.setCurrentPopup();
      this.onClose();
    },
    closeSmallModal() {
      this.setCurrentSmallPopup();
      this.onClose();
    },
    closeModalWithLogout() {
      this.logout();
      this.setCurrentPopup();
      this.onClose();
    },
    closePaymentSuccessModal() {
      this.markFirstDepositDone();
      this.setCurrentPopup();
      this.setCurrentDeposit(null);
      this.onClose();
    },
    closeModalHideOverlay() {
      this.setCurrentPopup();
      this.hideOverlay();
      this.onClose();
    },
    closeUserReportModal() {
      this.setCurrentSmallPopup(MODALS.SMALL_USER_PROFILE);
    },
    closeWhatIsNewPopup() {
      this.loaderAdd(document.body);
      this.setSettingsAnnouncementCurState(VERSION_TO_MIGRATE_TO)
        .then(() => {
          this.loaderRemove(document.body);
          this.closeModal();
        })
        .catch(() => {
          this.closeModal();
          this.loaderRemove(document.body);
        });
    },
    onClose() {
      if (this.currentPopupOptions && typeof this.currentPopupOptions.onClose !== 'undefined') {
        this.currentPopupOptions.onClose.apply(this);
      }
    },
    onRecaptchaChallengeComplete(token) {
      // When v2 captcha is completed, show loading state while API processes
      if (
        token &&
        this.currentPopupOptions &&
        typeof this.currentPopupOptions.onVerified === 'function'
      ) {
        this.recaptchaVerifying = true;
        this.currentPopupOptions.onVerified(token);
      }
    },
    addFundsClose() {
      this.$gtm.trackEvent(
        withMeta({
          event: 'eventTrackerTrigger',
          category: 'Deposit',
          action: 'click',
          label: 'Closing/Cancel Deposit Modal',
        }),
      );
      // Chain to next modal in priority order: COLLEGE_AFFILIATION → TWO_FACTOR_PROMPT
      if (this.collegeAffiliationEnabled && this.status && this.status.isUCAffilSet === null) {
        this.setCurrentPopup(MODALS.COLLEGE_AFFILIATION);
      } else if (this.twoFactorEnabled !== true && !constants.DISABLE_TWO_FACTOR_PROMPT) {
        this.setCurrentPopup(MODALS.TWO_FACTOR_PROMPT);
      } else {
        this.closeModal();
      }
    },
    // Called when ACCOUNT_VERIFY (Personal Details) modal is closed during registration
    closeAccountVerifyModal() {
      // Closed without completion - check 2FA as final step
      if (this.twoFactorEnabled !== true && !constants.DISABLE_TWO_FACTOR_PROMPT) {
        this.setCurrentPopup(MODALS.TWO_FACTOR_PROMPT);
      } else {
        this.closeModal();
      }
    },
    paymentSuccessDone() {
      // Chain to next modal in priority order: COLLEGE_AFFILIATION → TWO_FACTOR_PROMPT
      if (this.collegeAffiliationEnabled && this.status && this.status.isUCAffilSet === null) {
        this.setCurrentPopupWithDelay({ name: MODALS.COLLEGE_AFFILIATION });
      } else if (this.twoFactorEnabled !== true && !constants.DISABLE_TWO_FACTOR_PROMPT) {
        this.setCurrentPopup(MODALS.TWO_FACTOR_PROMPT);
      } else {
        this.closeModal();
      }
    },
    // Called when COLLEGE_AFFILIATION modal is closed during registration
    closeAffiliationsModal() {
      // College affiliation is second-to-last - 2FA prompt is final step
      if (this.twoFactorEnabled !== true && !constants.DISABLE_TWO_FACTOR_PROMPT) {
        this.setCurrentPopup(MODALS.TWO_FACTOR_PROMPT);
      } else {
        this.closeModal();
      }
    },
    // Called when affiliations form is submitted successfully
    onAffiliationsSubmitted() {
      // Same as close - 2FA prompt is final step
      if (this.twoFactorEnabled !== true && !constants.DISABLE_TWO_FACTOR_PROMPT) {
        this.setCurrentPopup(MODALS.TWO_FACTOR_PROMPT);
      } else {
        this.closeModal();
      }
    },
  },
  data() {
    return {
      MODALS,
      constants,
      recaptchaVerifying: false,
      gtmAddFunds: {
        virtualPage: '/Account/_DptFrmUsSgnUp',
        closing: {
          event: 'eventTrackerTrigger',
          category: 'Deposit',
          action: 'click',
          label: 'Closing Deposit Modal',
        },
      },
      gtmAccountVerify: {
        virtualPage: '_SgnUpDptIDV',
        closing: {
          event: 'eventTrackerTrigger',
          category: 'ID Verification',
          action: 'click',
          label: 'Closing ID Verification Modal',
        },
      },
      gtmLogin: {
        name: 'login',
        closing: {
          event: 'eventTrackerTrigger',
          category: 'Login',
          action: 'click',
          label: 'Closing Login Modal',
        },
      },
      gtmSignUp: {
        virtualPage: '/Account/SignUp',
        closing: {
          event: 'eventTrackerTrigger',
          category: 'Registration',
          action: 'click',
          label: 'Closing Registration Modal',
        },
      },
    };
  },
};
